import { activeInterviewRoundLabel } from './interviewRoundDisplay';
import { isPendingClientSubmission } from './applicationClientVisibility';

/**
 * ATS "Current Status" column + filter keys (aligned with server hiringConstants).
 */
/** All filter choices — always shown in the Current Status dropdown. */
export const CURRENT_STATUS_FILTER_OPTIONS = [
  // Interview schedule statuses
  { value: 'interview:scheduled', label: 'Interview — Scheduled' },
  { value: 'interview:confirmed', label: 'Interview — Confirmed' },
  { value: 'interview:reschedule_requested', label: 'Interview — Reschedule requested' },
  { value: 'interview:reschedule_pending_approval', label: 'Interview — Reschedule pending approval' },
  { value: 'interview:reschedule_ready_confirm', label: 'Interview — Reschedule approved (ready to confirm)' },
  { value: 'interview:cancelled', label: 'Interview — Cancelled' },
  { value: 'stage:interview', label: 'Interview — Awaiting schedule' },
  // Offer statuses
  { value: 'offer:pending', label: 'Offer — Awaiting candidate response' },
  { value: 'offer:negotiating', label: 'Offer — Under negotiation' },
  { value: 'offer:accepted', label: 'Offer — Accepted' },
  { value: 'offer:declined', label: 'Offer — Declined' },
  // Application stages
  { value: 'stage:submitted', label: 'Submitted' },
  { value: 'stage:internal_review', label: 'Internal review (not shared with client)' },
  { value: 'stage:selected', label: 'Selected' },
  { value: 'stage:shortlisted', label: 'Shortlisted' },
  { value: 'stage:not_selected', label: 'Not Selected' },
  { value: 'stage:joined', label: 'Joined' },
  { value: 'stage:relieved', label: 'Relieved' },
  { value: 'stage:rejected', label: 'Rejected' }
];

const STATUS_KEY_LABELS = Object.fromEntries(
  CURRENT_STATUS_FILTER_OPTIONS.map(o => [o.value, o.label])
);

/** Fine-grained key for column display and filtering. */
export function appCurrentStatusKey(app) {
  if (isPendingClientSubmission(app)) {
    return 'stage:internal_review';
  }
  if (app.stage === 'interview') {
    const sched = app.interviewSchedule;
    const status = sched?.status;
    if (!status) return 'stage:interview';
    if (status === 'reschedule_requested') {
      const summary = app.rescheduleApprovalSummary;
      if (summary?.allApproved) return 'interview:reschedule_ready_confirm';
      if (summary && !summary.allApproved && (summary.pending?.length || 0) > 0) {
        return 'interview:reschedule_pending_approval';
      }
      return 'interview:reschedule_requested';
    }
    return `interview:${status}`;
  }
  if (app.stage === 'offer') {
    return `offer:${app.offerStatus || 'pending'}`;
  }
  return `stage:${app.stage || 'submitted'}`;
}

/** Whether an application matches any selected Current Status filter value. */
export function appMatchesStatusFilters(app, filterValues) {
  if (!filterValues?.length) return true;
  const key = appCurrentStatusKey(app);
  return filterValues.some(filterValue => {
    if (filterValue === key) return true;
    // Parent bucket: any reschedule request (all sub-states)
    if (
      filterValue === 'interview:reschedule_requested' &&
      app.stage === 'interview' &&
      app.interviewSchedule?.status === 'reschedule_requested'
    ) {
      return true;
    }
    return false;
  });
}

/** Human-readable label for the Current Status column. */
export function appCurrentStatusLabel(app) {
  const key = appCurrentStatusKey(app);
  if (STATUS_KEY_LABELS[key]) return STATUS_KEY_LABELS[key];
  if (app.stage === 'interview') {
    const roundPart = `${activeInterviewRoundLabel(app)} — `;
    return roundPart + (app.interviewStatusLabel || 'Scheduled');
  }
  if (app.stage === 'offer') {
    return app.offerStatusLabel || app.offerStatus || 'Offer';
  }
  if (app.stage === 'selected') {
    return 'Selected — OK to schedule interview';
  }
  if (isPendingClientSubmission(app)) {
    return 'Internal review — not yet shared with client';
  }
  if (app.stage === 'not_selected') {
    return app.notSelectedReasonLabel
      ? `Not selected — ${app.notSelectedReasonLabel}`
      : 'Not selected';
  }
  if (app.stage === 'shortlisted') {
    return 'Shortlisted — awaiting offer';
  }
  return app.stage ? app.stage.replace(/-/g, ' ') : '—';
}
